import { useEffect, useMemo, useRef } from 'react';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';
import { GENERATED_ROLE_DEFAULTS_VERSION } from '@revelio/data-access/role-defaults';

import { kibanaLogger } from '../../../../shared/logging/kibana-logger';
import { useRoleTreeSelectionLists } from '../../../../shared/selection-lists/hooks/useRoleTreeSelectionLists';
import {
  PreferenceScope,
  UserPreferenceKey,
} from '../../../../shared/user-preferences/types';
import { PreferenceBrowserStorage } from '../../../../shared/user-preferences/utils/preference-session-storage';
import { reconcileVersionedDefaults } from '../../../../shared/user-preferences/utils/reconcileVersionedDefaults';
import { useToast } from '../../hooks/useToast';
import type { TreeFilter } from '../types/filters';

/** How many roles to seed when falling back to the live list. */
const DEFAULT_ROLE_COUNT = 5;

/**
 * Surfaces the role default-filter version reconciliation as a side effect and,
 * when both the saved preference and the baked-in defaults are stale, supplies
 * the first N roles from the live list as a fallback selection.
 *
 * - saved stale (baked current) → info toast ("out of date"), caller uses coded defaults.
 * - saved + baked stale → error toast + Kibana log, caller uses `firstFromLiveSelections`.
 *
 * Toasts/logs fire from an effect (never during the synchronous default-map build).
 */
export const useRoleDefaultsStaleNotice = ({
  enabled,
  currentRoleVersion,
}: {
  enabled: boolean;
  currentRoleVersion?: string;
}): { firstFromLiveSelections?: TreeFilter['selections'] } => {
  const toast = useToast();
  const notifiedRef = useRef<string | null>(null);

  const rolePrefs = PreferenceBrowserStorage.get(PreferenceScope.ROLE);
  const savedSelections = rolePrefs?.[UserPreferenceKey.DefaultFilters];
  const savedVersion =
    rolePrefs?.[UserPreferenceKey.Taxonomies]?.roleTaxonomyVersion;

  const result = reconcileVersionedDefaults({
    hasSaved: !!savedSelections && savedSelections.length > 0,
    savedVersion,
    currentVersion: currentRoleVersion,
    bakedVersion: GENERATED_ROLE_DEFAULTS_VERSION,
  });

  // Only fetch the live list when we actually need the fallback.
  const needLive = enabled && result.generatedStale;
  const roleSelectionPref = rolePrefs?.[UserPreferenceKey.Taxonomies]?.role;
  const roleSelection: CustomRoleTaxonomySelection =
    roleSelectionPref && roleSelectionPref !== 'adaptive'
      ? roleSelectionPref
      : { default: true };
  // k150 nodes plus their k7 parent — enough to rebuild the persisted
  // `<role_k7>_<role_k150>` selection id from the live tree.
  const liveLists = useRoleTreeSelectionLists({
    requiredKeys: ['role_k7', 'role_k150'],
    taxonomySelection: roleSelection,
    pause: !needLive,
  });

  const firstFromLiveSelections = useMemo<
    TreeFilter['selections'] | undefined
  >(() => {
    const roleK150 = liveLists?.role_k150;
    if (!needLive || !roleK150) return undefined;
    return roleK150
      .flatMap((role) => {
        const parentK7 = role.role_k7;
        if (!role.id || !parentK7 || typeof parentK7 !== 'string') return [];
        return [
          {
            id: `${parentK7}_${role.id}`,
            name: role.shortName ?? role.longName ?? role.id,
            levelId: 'role_k150' as const,
          },
        ];
      })
      .slice(0, DEFAULT_ROLE_COUNT);
  }, [needLive, liveLists]);

  useEffect(() => {
    if (!enabled) return;
    const key = `${result.source}:${currentRoleVersion ?? ''}`;
    if (notifiedRef.current === key) return;

    if (result.generatedStale) {
      notifiedRef.current = key;
      kibanaLogger?.error(
        `Role default filters stale: generated ${GENERATED_ROLE_DEFAULTS_VERSION} != live ${currentRoleVersion}`
      );
      const id = 'role-defaults-generated-stale';
      if (!toast.isActive(id)) {
        toast({
          id,
          title: 'Default roles could not be loaded',
          description:
            'A temporary role selection is being shown. Please review and re-select your roles.',
          status: 'error',
          duration: 8000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } else if (result.savedStale) {
      notifiedRef.current = key;
      const id = 'role-defaults-out-of-date';
      if (!toast.isActive(id)) {
        toast({
          id,
          title: 'Your saved role defaults are out of date',
          description:
            'The role taxonomy was updated, so default selections were applied. Please review them.',
          status: 'info',
          duration: 6000,
          isClosable: true,
          position: 'top-right',
        });
      }
    }
  }, [
    enabled,
    result.source,
    result.savedStale,
    result.generatedStale,
    currentRoleVersion,
    toast,
  ]);

  return { firstFromLiveSelections };
};
